import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  X, Sparkles, Building2, Shield, Newspaper, Users, 
  ArrowRight, Crown, PartyPopper, Zap, Music, Headphones, Loader2,
  Briefcase, Mic2, Store, Handshake, Star, ChevronRight, QrCode
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { publicAuthApi } from '../lib/api';
import { toast } from 'sonner';

const portalIcons = {
  admin: Shield,
  producer: Building2,
  press: Newspaper,
  partner: Users,
  dj: Headphones,
  validator: QrCode,
};

const portalColors = {
  admin: { bg: 'from-purple-500/20 to-purple-600/10', border: 'border-purple-500/30', text: 'text-purple-400', accent: '#A855F7' },
  producer: { bg: 'from-amber-500/20 to-amber-600/10', border: 'border-amber-500/30', text: 'text-amber-400', accent: '#F59E0B' },
  press: { bg: 'from-cyan-500/20 to-cyan-600/10', border: 'border-cyan-500/30', text: 'text-cyan-400', accent: '#06B6D4' },
  partner: { bg: 'from-emerald-500/20 to-emerald-600/10', border: 'border-emerald-500/30', text: 'text-emerald-400', accent: '#10B981' },
  dj: { bg: 'from-pink-500/20 to-pink-600/10', border: 'border-pink-500/30', text: 'text-pink-400', accent: '#EC4899' },
  validator: { bg: 'from-orange-500/20 to-orange-600/10', border: 'border-orange-500/30', text: 'text-orange-400', accent: '#F97316' },
};

// Personalized welcome messages by portal type
const getPersonalizedWelcome = (accountType, userName) => {
  const messages = {
    dj: {
      greeting: `¡Qué onda, ${userName}!`,
      subtitle: 'Tu música mueve a la gente 🎧'
    },
    producer: {
      greeting: `¡Hola, ${userName}!`,
      subtitle: 'Listo para crear eventos increíbles'
    },
    admin: {
      greeting: `Bienvenido, ${userName}`,
      subtitle: 'Panel de control listo'
    },
    press: {
      greeting: `¡Hola, ${userName}!`,
      subtitle: 'Las noticias te esperan'
    },
    partner: {
      greeting: `¡Hola, ${userName}!`,
      subtitle: 'Tu espacio de colaboración'
    },
    validator: {
      greeting: `¡Hola, ${userName}!`,
      subtitle: 'Listo para validar entradas'
    }
  };
  return messages[accountType] || { greeting: `¡Hola, ${userName}!`, subtitle: 'Bienvenido de vuelta' };
};

// Available features/opportunities for regular users
const availableFeatures = [
  {
    id: 'events',
    icon: PartyPopper,
    title: 'Comprar Entradas',
    description: 'Descubrí los mejores eventos y comprá tus tickets',
    url: '/eventos',
    color: '#D4AF37',
    tag: 'Eventos'
  },
  {
    id: 'fullbravx',
    icon: Star,
    title: 'FULL BRAVX',
    description: 'Suscribite y accedé a beneficios exclusivos',
    url: '/fullbravx',
    color: '#8B5CF6',
    tag: 'Suscripción'
  },
  {
    id: 'jobs',
    icon: Briefcase,
    title: 'Bolsa de Trabajo',
    description: 'Encontrá empleos en la industria de eventos',
    url: '/bolsa-trabajo',
    color: '#06B6D4',
    tag: 'Empleos'
  },
  {
    id: 'dj',
    icon: Mic2,
    title: 'Soy DJ / Artista',
    description: 'Creá tu perfil y postulate para tocar',
    url: '/mi-cuenta/dj',
    color: '#EC4899',
    tag: 'Perfil'
  },
  {
    id: 'producer',
    icon: Building2,
    title: 'Tengo una Productora',
    description: 'Vendé tus eventos en nuestra plataforma',
    url: '/productoras/aplicar',
    color: '#F59E0B',
    tag: 'Registro'
  },
  {
    id: 'partner',
    icon: Handshake,
    title: 'Quiero ser Partner',
    description: 'Asociate con BRAVO y obtené beneficios',
    url: '/fullbravx',
    color: '#10B981',
    tag: 'Info'
  }
];

const WelcomeModal = ({ isOpen, onClose, userName, linkedAccounts = [], isNewUser = false, isFullBravx = false }) => {
  const navigate = useNavigate();
  const [hoveredPortal, setHoveredPortal] = useState(null);
  const [loadingPortal, setLoadingPortal] = useState(null);
  const [hoveredFeature, setHoveredFeature] = useState(null);

  const handlePortalClick = async (account) => {
    // DJ accounts don't need special login - they're part of the user's account
    if (account.type === 'dj') {
      onClose();
      setTimeout(() => {
        navigate(account.portal_url);
      }, 300);
      return;
    }

    setLoadingPortal(account.type);
    try {
      // Determine the correct portal type for login
      let portalType = account.type;
      if (account.type === 'producer' && account.role === 'admin') {
        portalType = 'producer_admin';
      }
      
      console.log('[WelcomeModal] Portal login request for:', portalType);
      const response = await publicAuthApi.portalLogin(portalType);
      console.log('[WelcomeModal] Portal login response:', response.data);
      
      const { access_token, portal_url } = response.data;
      
      // Store the token based on portal type
      if (account.type === 'admin') {
        // Clear ALL other tokens to avoid conflicts
        localStorage.removeItem('public_token');
        localStorage.removeItem('producer_token');
        localStorage.removeItem('press_token');
        localStorage.removeItem('partner_token');
        localStorage.removeItem('validator_token');
        localStorage.removeItem('validatorToken');
        
        // Store admin token
        localStorage.setItem('token', access_token);
        const userData = {
          id: response.data.account?.id,
          username: response.data.account?.username,
          full_name: response.data.account?.full_name,
          email: response.data.account?.email,
          is_owner: response.data.account?.is_owner,
          is_active: true
        };
        localStorage.setItem('user', JSON.stringify(userData));
        
        console.log('[WelcomeModal] Admin token stored successfully');
        console.log('[WelcomeModal] Token in localStorage:', localStorage.getItem('token')?.substring(0, 30) + '...');
        console.log('[WelcomeModal] Redirecting to:', portal_url);
      } else if (account.type === 'producer') {
        localStorage.setItem('producer_token', access_token);
      } else if (account.type === 'press') {
        localStorage.setItem('press_token', access_token);
      } else if (account.type === 'partner') {
        localStorage.setItem('partner_token', access_token);
      } else if (account.type === 'validator') {
        localStorage.setItem('validator_token', access_token);
        localStorage.setItem('validatorToken', access_token);
      }
      
      onClose();
      // Use window.location for full page reload to ensure context resets
      window.location.href = portal_url;
    } catch (error) {
      console.error('Portal login error:', error);
      toast.error(error.response?.data?.detail || 'Error al acceder al portal');
      setLoadingPortal(null);
    }
  };

  const handleFeatureClick = (feature) => {
    onClose();
    setTimeout(() => {
      navigate(feature.url);
    }, 300);
  };

  const handleContinue = () => {
    onClose();
  };

  if (!isOpen) return null;

  const hasLinkedAccounts = linkedAccounts.length > 0;
  
  // Get primary account type for personalized welcome (prioritize DJ)
  const primaryAccount = linkedAccounts.find(acc => acc.type === 'dj') || linkedAccounts[0];
  const personalizedWelcome = primaryAccount 
    ? getPersonalizedWelcome(primaryAccount.type, userName)
    : { greeting: `¡Hola, ${userName}!`, subtitle: isNewUser ? '¡Bienvenido a BRAVO!' : 'Descubrí todo lo que podés hacer' };

  // Filter features based on user status (hide FULL BRAVX if already subscribed)
  const filteredFeatures = availableFeatures.filter(f => {
    if (f.id === 'fullbravx' && isFullBravx) return false;
    return true;
  });

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[100]"
          />

          {/* Modal Container - Centered with flexbox */}
          <div className="fixed inset-0 z-[101] flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              className="w-full max-w-md max-h-[85vh] flex flex-col"
            >
              <div className="relative bg-gradient-to-br from-zinc-900 via-zinc-900 to-zinc-950 border border-zinc-800 rounded-2xl overflow-hidden shadow-2xl flex flex-col max-h-full">
                {/* Decorative elements */}
                <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-[#D4AF37] to-transparent" />
                <div className="absolute -top-20 -right-20 w-40 h-40 bg-[#D4AF37]/10 rounded-full blur-3xl" />
                <div className="absolute -bottom-20 -left-20 w-40 h-40 bg-[#D4AF37]/5 rounded-full blur-3xl" />

                {/* Close button */}
                <button
                  onClick={onClose}
                  className="absolute top-4 right-4 w-8 h-8 rounded-full bg-zinc-800/50 hover:bg-zinc-700 flex items-center justify-center transition-colors z-10"
                >
                  <X size={16} className="text-zinc-400" />
                </button>

                {/* Content */}
                <div className="relative p-6 overflow-y-auto flex-1">
                  {/* Header */}
                <div className="text-center mb-5">
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ delay: 0.2, type: 'spring' }}
                    className="w-14 h-14 mx-auto mb-3 rounded-xl bg-gradient-to-br from-[#D4AF37]/20 to-[#D4AF37]/5 border border-[#D4AF37]/30 flex items-center justify-center"
                  >
                    {isNewUser ? (
                      <PartyPopper size={28} className="text-[#D4AF37]" />
                    ) : (
                      <Sparkles size={28} className="text-[#D4AF37]" />
                    )}
                  </motion.div>

                  <motion.h2
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 }}
                    className="text-xl font-black text-white mb-1"
                  >
                    {isNewUser ? '¡Bienvenido a BRAVO!' : personalizedWelcome.greeting}
                  </motion.h2>

                  <motion.p
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.4 }}
                    className="text-zinc-400 text-sm"
                  >
                    {isNewUser 
                      ? 'Tu cuenta ha sido creada' 
                      : personalizedWelcome.subtitle
                    }
                  </motion.p>
                </div>

                {/* Linked Accounts */}
                {hasLinkedAccounts && (
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.5 }}
                    className="space-y-2 mb-4"
                  >
                    <p className="text-[10px] font-semibold text-zinc-500 uppercase tracking-wider mb-2">
                      Tus Accesos
                    </p>

                    {linkedAccounts.map((account, index) => {
                      const Icon = portalIcons[account.type] || Shield;
                      const colors = portalColors[account.type] || portalColors.admin;
                      const isHovered = hoveredPortal === index;
                      const isLoading = loadingPortal === account.type;

                      return (
                        <motion.button
                          key={index}
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: 0.5 + index * 0.1 }}
                          onClick={() => handlePortalClick(account)}
                          onMouseEnter={() => setHoveredPortal(index)}
                          onMouseLeave={() => setHoveredPortal(null)}
                          disabled={isLoading}
                          className={`
                            w-full p-3 rounded-xl border transition-all duration-300
                            bg-gradient-to-r ${colors.bg} ${colors.border}
                            hover:scale-[1.02] group text-left
                            ${isLoading ? 'opacity-70 cursor-wait' : ''}
                          `}
                        >
                          <div className="flex items-center gap-3">
                            <div 
                              className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0`}
                              style={{ background: `linear-gradient(135deg, ${colors.accent}30, ${colors.accent}10)` }}
                            >
                              {isLoading ? (
                                <Loader2 size={18} className="animate-spin" style={{ color: colors.accent }} />
                              ) : (
                                <Icon size={18} style={{ color: colors.accent }} />
                              )}
                            </div>

                            <div className="flex-1 min-w-0">
                              <p className="font-semibold text-white text-sm truncate">
                                {account.name}
                              </p>
                              <p className={`text-xs ${colors.text} truncate`}>
                                {isLoading ? 'Accediendo...' : account.label}
                              </p>
                            </div>

                            {!isLoading && (
                              <ArrowRight 
                                size={18} 
                                className={`${colors.text} opacity-50 group-hover:opacity-100 shrink-0`} 
                              />
                            )}
                          </div>
                        </motion.button>
                      );
                    })}
                  </motion.div>
                )}

                {/* Available Features for Regular Users */}
                {!hasLinkedAccounts && (
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.5 }}
                    className="space-y-2 mb-4 overflow-y-auto"
                  >
                    <p className="text-[10px] font-semibold text-zinc-500 uppercase tracking-wider mb-2">
                      ¿Qué te gustaría hacer?
                    </p>

                    {filteredFeatures.map((feature, index) => {
                      const Icon = feature.icon;
                      const isHovered = hoveredFeature === index;

                      return (
                        <motion.button
                          key={feature.id}
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: 0.5 + index * 0.08 }}
                          onClick={() => handleFeatureClick(feature)}
                          onMouseEnter={() => setHoveredFeature(index)}
                          onMouseLeave={() => setHoveredFeature(null)}
                          className="w-full p-3 rounded-xl border border-zinc-800 bg-zinc-900/50 hover:bg-zinc-800/50 hover:border-zinc-700 transition-all duration-300 hover:scale-[1.01] group text-left"
                        >
                          <div className="flex items-center gap-3">
                            <div 
                              className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0"
                              style={{ background: `linear-gradient(135deg, ${feature.color}25, ${feature.color}10)` }}
                            >
                              <Icon size={16} style={{ color: feature.color }} />
                            </div>

                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <p className="font-semibold text-white text-sm truncate">
                                  {feature.title}
                                </p>
                                <span 
                                  className="text-[9px] px-1.5 py-0.5 rounded-full font-medium shrink-0"
                                  style={{ 
                                    backgroundColor: `${feature.color}20`, 
                                    color: feature.color 
                                  }}
                                >
                                  {feature.tag}
                                </span>
                              </div>
                              <p className="text-xs text-zinc-500 truncate">
                                {feature.description}
                              </p>
                            </div>

                            <ChevronRight 
                              size={16} 
                              className="text-zinc-600 group-hover:text-zinc-400 shrink-0 transition-colors" 
                            />
                          </div>
                        </motion.button>
                      );
                    })}
                  </motion.div>
                )}

                {/* Continue Button */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.7 }}
                  className="pt-2"
                >
                  <button
                    onClick={handleContinue}
                    className="w-full py-3 px-4 rounded-xl bg-gradient-to-r from-[#D4AF37] to-[#B8962D] hover:from-[#E5C04B] hover:to-[#D4AF37] text-black font-semibold transition-all duration-300 flex items-center justify-center gap-2 text-sm"
                  >
                    <span>Continuar</span>
                    <Zap size={14} />
                  </button>
                </motion.div>
              </div>
            </div>
          </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>
  );
};

export default WelcomeModal;
